import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import { Button, DatePicker, Popover, PopoverTrigger } from '@rematter/ui-web';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface BookingMarkAsShippedDialogProps {
  isDialogOpen: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  initialShippedDate?: CalendarDate;
  onClose: () => void;
  onConfirm: (shippedDate?: CalendarDate) => Promise<void>;
}

export const BookingMarkAsShippedDialog: React.FC<
  BookingMarkAsShippedDialogProps
> = ({
  children,
  isDialogOpen,
  isLoading,
  initialShippedDate,
  onClose,
  onConfirm,
}) => {
  const t = useTranslations();
  const [shippedDate, setShippedDate] = React.useState<DateValue | null>(
    initialShippedDate ?? null,
  );

  const handleConfirm = React.useCallback(async () => {
    if (!shippedDate) return;

    await onConfirm(shippedDate as CalendarDate);
  }, [shippedDate, onConfirm]);

  const handleDiscard = React.useCallback(() => {
    onClose();
    setShippedDate(initialShippedDate ?? null);
  }, [initialShippedDate, onClose]);

  return (
    <PopoverTrigger>
      {children}
      <Popover
        isOpen={isDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleDiscard();
        }}
        className="@BookingMarkAsShippedDialog"
      >
        <div className="flex w-[320px] flex-col gap-4 rounded-md bg-white p-4 shadow-lg">
          <DatePicker
            value={shippedDate}
            onChange={setShippedDate}
            className="w-full"
            label={t.shipBookingDialog.title}
            isRequired={true}
            isDisabled={false}
          />

          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" color="neutral" onPress={handleDiscard}>
              {t.shipBookingDialog.cancel}
            </Button>
            <Button
              variant="solid"
              onPress={handleConfirm}
              isLoading={isLoading}
              isDisabled={!shippedDate}
            >
              {t.shipBookingDialog.confirm}
            </Button>
          </div>
        </div>
      </Popover>
    </PopoverTrigger>
  );
};
